import { Fragment, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, type SubscriptionStatus } from '@structural-climbing/ui'

const STATUS_TEXT: Record<SubscriptionStatus, string> = {
  active: 'text-success',
  scheduled: 'text-primary',
  frozen: 'text-sky-700 dark:text-sky-300',
  expired: 'text-danger',
  depleted: 'text-warning',
  cancelled: 'text-foreground/45',
}

/** "● Active" in the status colour, for the grey line under a name. */
export function StatusWord({ status }: { status: SubscriptionStatus | undefined }) {
  const { t } = useTranslation()
  return (
    <span
      className={`inline-flex items-center gap-1 font-medium ${status ? STATUS_TEXT[status] : 'text-foreground/45'}`}
    >
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />
      {status ? t(`members.subStatus_${status}`) : t('members.noSubscription')}
    </span>
  )
}

/** The "2 children" link that opens a parent's children under their row. */
export function ChildrenToggle({
  count,
  isOpen,
  onToggle,
}: {
  count: number
  isOpen: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  return (
    // Inside a row that opens the profile: this link only opens the children.
    <span onClick={(e) => e.stopPropagation()}>
      <Button
        size="sm"
        variant="light"
        aria-expanded={isOpen}
        onPress={onToggle}
        className="h-auto min-w-0 px-0 text-xs text-primary underline underline-offset-2 data-[hover=true]:bg-transparent"
      >
        {t('members.childrenCount', { count })}
      </Button>
    </span>
  )
}

interface Props {
  /** A `MemberAvatar`. It keeps its size however long the text gets. */
  avatar: ReactNode
  /** The name, with any star or small badge after it. */
  name: ReactNode
  /** Opens the profile when the name is clicked. */
  onOpen?: () => void
  openLabel?: string
  status: SubscriptionStatus | undefined
  /** Shown after the status, joined with " · ": plan, renewal, children link. */
  details: ReactNode[]
  /** The one action (Check-in, Renew, Sell a plan) and the ⋮ menu. */
  actions?: ReactNode
}

/**
 * One person in the Check-in and Members lists: avatar, name, then a grey line
 * with the status word and the plan. On a desktop the actions sit on the right;
 * on a phone they move under the text, so the name always gets the full width.
 */
export function MemberRow({ avatar, name, onOpen, openLabel, status, details, actions }: Props) {
  const parts = [<StatusWord key="status" status={status} />, ...details.filter(Boolean)]
  return (
    <div className="flex flex-col gap-2 py-3 md:flex-row md:items-center md:gap-4">
      {/* Avatar, name and the grey line all open the profile. The name stays a
          real button for the keyboard; the rest is a larger target for the mouse. */}
      <div
        className={`flex min-w-0 flex-1 items-start gap-3 md:items-center ${onOpen ? 'cursor-pointer' : ''}`}
        onClick={onOpen}
      >
        <div className="shrink-0">{avatar}</div>
        <div className="min-w-0 flex-1">
          {onOpen ? (
            <button
              type="button"
              className="max-w-full cursor-pointer text-left"
              onClick={(e) => {
                e.stopPropagation()
                onOpen()
              }}
              title={openLabel}
            >
              {name}
            </button>
          ) : (
            name
          )}
          <p className="text-xs leading-relaxed text-foreground/55">
            {parts.map((part, i) => (
              <Fragment key={i}>
                {i > 0 && ' · '}
                {typeof part === 'string' ? <span>{part}</span> : part}
              </Fragment>
            ))}
          </p>
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center justify-end gap-1.5">{actions}</div>}
    </div>
  )
}
